/**
 * Tax & Reports - Year-end tax info for accountant and minor safe settings.
 *
 * - Explains how taxes are set up (6+ pieces, cakes exempt, 13% HST). Tammy cannot change rules.
 * - Minor settings: tax display label, HST/Business number (for receipts/reports).
 * - Tax report: date range, summary, by-month breakdown, CSV export.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  HiDocumentReport,
  HiInformationCircle,
  HiDownload,
  HiRefresh,
  HiCheck,
  HiExclamation,
  HiLockClosed,
} from 'react-icons/hi';
import { adminFetch, adminHeaders } from '../lib/adminApi';

const API_URL = import.meta.env.VITE_API_URL || '';

const TAX_LABEL_OPTIONS = [
  { value: 'HST (13%)', label: 'HST (13%)' },
  { value: 'Tax', label: 'Tax' },
  { value: 'GST/HST', label: 'GST/HST' },
];

interface TaxReportSummary {
  totalSales: number;
  totalHst: number;
  totalRevenue: number;
  orderCount: number;
}

interface TaxReportByMonth {
  month: string;
  totalSales: number;
  totalHst: number;
  totalRevenue: number;
  orderCount: number;
}

interface TaxReportOrder {
  order_number: string;
  created_at: string;
  customer_name: string;
  subtotal: string;
  hst: string;
  total: string;
  status: string;
  payment_status: string;
}

interface TaxReportResponse {
  summary: TaxReportSummary;
  byMonth: TaxReportByMonth[];
  orders: TaxReportOrder[];
}

export default function TaxReports() {
  const [settings, setSettings] = useState({ tax_display_label: 'HST (13%)', hst_business_number: '' });
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [report, setReport] = useState<TaxReportResponse | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await adminFetch(API_URL ? `${API_URL}/api/settings` : '/api/settings');
      if (!res.ok) return;
      const data = await res.json();
      setSettings({
        tax_display_label: data.tax_display_label ?? 'HST (13%)',
        hst_business_number: data.hst_business_number ?? '',
      });
    } catch {
      // keep defaults
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    setReportError(null);
    try {
      const res = await adminFetch(API_URL ? `${API_URL}/api/settings` : '/api/settings', {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(settings),
      });
      if (!res.ok) throw new Error('Failed to save');
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch {
      setReportError('Failed to save settings.');
    } finally {
      setSettingsSaving(false);
    }
  };

  const runReport = async () => {
    if (!reportFrom || !reportTo) {
      setReportError('Please select both From and To dates.');
      return;
    }
    setReportLoading(true);
    setReportError(null);
    setReport(null);
    try {
      const url = `${API_URL ? API_URL : ''}/api/orders/tax-report?from=${encodeURIComponent(reportFrom)}&to=${encodeURIComponent(reportTo)}`;
      const res = await adminFetch(url);
      if (!res.ok) throw new Error('Failed to load report');
      const data: TaxReportResponse = await res.json();
      setReport(data);
    } catch {
      setReportError('Failed to load tax report. Check dates and try again.');
    } finally {
      setReportLoading(false);
    }
  };

  const downloadCsv = () => {
    if (!report) return;
    const base = `Tax Report ${reportFrom} to ${reportTo}\n`;
    const hstLine = settings.hst_business_number ? `HST/Business #: ${settings.hst_business_number}\n` : '';
    const summaryLine = `Total Sales,Total HST,Total Revenue,Order Count\n${report.summary.totalSales},${report.summary.totalHst},${report.summary.totalRevenue},${report.summary.orderCount}\n\n`;
    const headers = 'Order #,Date,Customer,Subtotal,HST,Total,Status,Payment\n';
    const rows = report.orders
      .map(
        (o) =>
          `${o.order_number},${o.created_at},${(o.customer_name || '').replace(/,/g, ' ')},${o.subtotal},${o.hst},${o.total},${o.status},${o.payment_status}`
      )
      .join('\n');
    const csv = base + hstLine + summaryLine + headers + rows;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `tax-report-${reportFrom}-${reportTo}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const currentYear = new Date().getFullYear();
  const defaultFrom = `${currentYear}-01-01`;
  const defaultTo = `${currentYear}-12-31`;

  const reportFrom = fromDate || defaultFrom;
  const reportTo = toDate || defaultTo;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Tax & Reports</h1>
        <p className="text-gray-500 mt-1">Year-end info for your accountant and minor tax display settings</p>
      </div>

      {reportError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <HiExclamation className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-red-800">{reportError}</p>
        </div>
      )}

      {/* How taxes are set up – cannot change */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <HiLockClosed className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <HiInformationCircle className="w-5 h-5 text-amber-600" />
              How taxes are set up
            </h2>
            <p className="text-sm text-gray-700 mt-2">
              We follow Ontario CRA rules: <strong>6 or more baked items</strong> (e.g. 6 cookies, 1 dozen = 12 pieces) = no HST on those. <strong>Cakes</strong> (e.g.
              custom cakes, cheesecakes) = no HST. <strong>Sandwiches &amp; savory</strong> = always taxable. Other items <strong>13% HST</strong> on the taxable portion only.
            </p>
            <p className="text-sm text-gray-700 mt-2">
              The <strong>rate and these rules are fixed</strong> in the system. You cannot turn them off or change them
              in this portal.
            </p>
            <p className="text-sm text-amber-800 font-medium mt-2">
              If you need different rules, a different rate, or tax for another province, contact your developer to have
              it changed in the system.
            </p>
          </div>
        </div>
      </div>

      {/* Minor safe settings */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Minor settings (safe to change)</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tax label (what customers see)</label>
            <select
              aria-label="Tax label shown to customers"
              value={settings.tax_display_label}
              onChange={(e) => setSettings((s) => ({ ...s, tax_display_label: e.target.value }))}
              className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {TAX_LABEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Cosmetic only – does not change the amount of tax.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">HST / Business number</label>
            <input
              type="text"
              value={settings.hst_business_number}
              onChange={(e) => setSettings((s) => ({ ...s, hst_business_number: e.target.value }))}
              placeholder="e.g. 123456789 RT0001"
              className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Shown on the CSV export for your accountant.</p>
          </div>
          <div>
          <button
            onClick={handleSaveSettings}
            disabled={settingsSaving}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              settingsSaved ? 'bg-green-600 text-white' : 'bg-primary-600 text-white hover:bg-primary-700'
            } disabled:opacity-50`}
          >
            {settingsSaving ? (
              <HiRefresh className="w-5 h-5 animate-spin" />
            ) : settingsSaved ? (
              <>
                <HiCheck className="w-5 h-5" />
                Saved
              </>
            ) : (
              <>
                <HiCheck className="w-5 h-5" />
                Save settings
              </>
            )}
          </button>
          </div>
        </div>
      </div>

      {/* Tax report for accountant */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <HiDocumentReport className="w-5 h-5 text-primary-600" />
          Tax report for accountant
        </h2>
        <div className="flex flex-wrap items-end gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <input
              type="date"
              aria-label="Report start date"
              value={reportFrom}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input
              type="date"
              aria-label="Report end date"
              value={reportTo}
              onChange={(e) => setToDate(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <button
            onClick={runReport}
            disabled={reportLoading}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {reportLoading ? <HiRefresh className="w-5 h-5 animate-spin" /> : null}
            Run report
          </button>
        </div>

        {report && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Total sales (subtotal)</p>
                <p className="text-xl font-semibold text-gray-900">${report.summary.totalSales.toFixed(2)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Total HST collected</p>
                <p className="text-xl font-semibold text-gray-900">${report.summary.totalHst.toFixed(2)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Total revenue</p>
                <p className="text-xl font-semibold text-primary-600">${report.summary.totalRevenue.toFixed(2)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Order count</p>
                <p className="text-xl font-semibold text-gray-900">{report.summary.orderCount}</p>
              </div>
            </div>

            {report.byMonth.length > 0 && (
              <div className="mb-6 overflow-x-auto">
                <h3 className="text-sm font-medium text-gray-700 mb-2">By month</h3>
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 pr-4">Month</th>
                      <th className="text-right py-2 pr-4">Sales</th>
                      <th className="text-right py-2 pr-4">HST</th>
                      <th className="text-right py-2 pr-4">Revenue</th>
                      <th className="text-right py-2">Orders</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.byMonth.map((row) => (
                      <tr key={row.month} className="border-b border-gray-100">
                        <td className="py-2 pr-4">{row.month}</td>
                        <td className="text-right pr-4">${row.totalSales.toFixed(2)}</td>
                        <td className="text-right pr-4">${row.totalHst.toFixed(2)}</td>
                        <td className="text-right pr-4">${row.totalRevenue.toFixed(2)}</td>
                        <td className="text-right">{row.orderCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <button
              onClick={downloadCsv}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <HiDownload className="w-5 h-5" />
              Download CSV for accountant
            </button>
          </>
        )}
      </div>
    </div>
  );
}
